import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { sanitize as sanitizeLink } from 'quill/formats/link';
import type { ImgHTMLAttributes } from 'react';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import SVGIcon from '../../components/SVGIcon';
import dropzoneImage from '../../icons/non-prefilled/28x28/dropzoneImage.svg';
import { useUIElementResize } from '../UIElementResizeContext';
import * as styles from './ImageNode.module.scss';
import { EDITOR_IMAGE_ACCEPT } from '../editorImageMimeTypes';

export const DROPZONE_WIDTH = 318;

function sanitize(url: string) {
  return sanitizeLink(url, ['https', 'data']) ? url : '//:0';
}

export const ALT = 'User-uploaded Image';
const TRANSITION = {
  type: 'spring',
  damping: 30,
  stiffness: 300,
  mass: 0.2,
} as const;
const DROPZONE_GAP = 24;
const PADDING = 4;

export type DropSide = 'left' | 'right';

function useImageDropzone({
  onDrop,
  containerRef,
  disabled,
}: {
  onDrop?: (files: File[], side: DropSide) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  disabled?: boolean;
}) {
  const [xPosition, setXPosition] = React.useState<number | null>(null);

  const containerWidth =
    containerRef.current?.getBoundingClientRect().width ?? 0;

  const dropzone = (() => {
    const dropzoneWidth = Math.min(containerWidth / 2, DROPZONE_WIDTH);
    const delta = dropzoneWidth + DROPZONE_GAP;
    if (xPosition == null) return null;
    const side: DropSide = xPosition > containerWidth / 2 ? 'right' : 'left';
    const translateX = side === 'right' ? -delta : delta;

    return {
      translateX,
      side,
      width: dropzoneWidth,
      translateInitial: side === 'right' ? containerWidth : -containerWidth,
      translateAnimate:
        side === 'right'
          ? containerWidth + translateX + DROPZONE_GAP - PADDING
          : -PADDING,
    };
  })();

  const { getRootProps } = useDropzone({
    onDrop(files) {
      if (dropzone) {
        onDrop?.(files, dropzone.side);
      }
      setXPosition(null);
    },
    accept: EDITOR_IMAGE_ACCEPT,
    disabled,
    onDragOver: (e) => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (containerRect == null) return;
      setXPosition(e.clientX - containerRect.left);
    },
    onDragLeave: () => setXPosition(null),
  });

  return {
    getRootProps,
    dropzone,
  };
}

interface ImageNodeProps {
  src: string;
  uploading: boolean;
  width?: number;
  className?: string;
  containerClassName?: string;
  alt?: string;
  onLoad?: ImgHTMLAttributes<HTMLImageElement>['onLoad'];
  onDrop?: (files: File[], side: DropSide) => void;
}

const ImageNode = ({
  src,
  uploading,
  width,
  className,
  containerClassName,
  alt = ALT,
  onLoad,
  onDrop,
}: ImageNodeProps) => {
  const onResize = useUIElementResize();
  const containerRef = React.useRef<HTMLDivElement>(null);

  const { getRootProps, dropzone } = useImageDropzone({
    onDrop,
    disabled: uploading,
    containerRef,
  });

  return (
    <div
      {...(onDrop ? getRootProps({ ref: containerRef }) : {})}
      className={classNames(styles.container, containerClassName)}
    >
      <motion.img
        // Setting this allows us to select carousels in read mode
        tabIndex={-1}
        className={classNames('image-node', className, {
          [styles.uploading]: uploading,
        })}
        onLoad={(e) => {
          onLoad?.(e);
          onResize();
        }}
        initial={false}
        animate={{
          transform: dropzone
            ? `translateX(${dropzone.translateX}px)`
            : undefined,
        }}
        width={width}
        onError={onResize}
        src={sanitize(src)}
        alt={alt}
        transition={TRANSITION}
      />
      {uploading && <div className={styles.progress} />}
      <AnimatePresence>
        {dropzone && (
          <motion.div
            transition={TRANSITION}
            initial={{
              transform: `translateX(${dropzone.translateInitial}px)`,
            }}
            animate={{
              transform: `translateX(${dropzone.translateAnimate}px)`,
            }}
            style={{ width: dropzone.width }}
            className={styles.dropzone}
          >
            <div className={styles.dropzoneInner}>
              <SVGIcon className={styles.dropzoneIcon} size={28}>
                {dropzoneImage}
              </SVGIcon>
              <div className={styles.label}>Drop images to upload</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImageNode;
